import { useState } from "react";
import { createCheckout } from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { CreditCard, Banknote, ArrowRight, Copy, Check, Loader2 } from "lucide-react";

const METHODS = [
  { id: "stripe", name: "Credit / Debit Card", desc: "Visa, Mastercard, Amex — processed securely via Stripe", Icon: CreditCard, color: "#1a6b3c" },
  { id: "zelle", name: "Zelle", desc: "Send directly from your bank app", Icon: Banknote, color: "#6d1ed4" },
  { id: "cashapp", name: "Cash App", desc: "Send to $LowKeyRents", Icon: Banknote, color: "#00d632" },
  { id: "square", name: "Square", desc: "Pay via Square Cash", Icon: Banknote, color: "#006aff" },
];

export default function PaymentMethodSelector({ paymentType, listingId, amount, buttonLabel, buttonClass }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualInfo, setManualInfo] = useState(null);
  const [copied, setCopied] = useState(false);

  const fee = Math.round(amount * 3) / 100;
  const total = (amount + fee).toFixed(2);

  const handlePay = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await createCheckout(paymentType, window.location.origin, listingId);
      if (selected === "stripe") {
        // Redirect to Stripe checkout
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleManualPay = async (method) => {
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + "/api";
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ payment_type: paymentType, payment_method: method, listing_id: listingId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (res.ok) {
        setManualInfo(data);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const copyMemo = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="pay-btn" className={buttonClass || "w-full rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold"}>
          {buttonLabel || "Pay Now"}
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white rounded-2xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-['Fraunces'] font-black text-xl">
            {manualInfo ? "Payment Instructions" : "Choose Payment Method"}
          </DialogTitle>
        </DialogHeader>

        {manualInfo ? (
          <div className="space-y-4" data-testid="manual-payment-info">
            <div className="bg-[#faf8f3] rounded-xl p-4 border border-black/[0.06]">
              <p className="text-sm text-[#0d0d0d] leading-relaxed">{manualInfo.instructions}</p>
            </div>
            <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-black/[0.06]">
              <div>
                <p className="text-[11px] text-[#888] font-medium">Reference Code</p>
                <p className="text-lg font-black text-[#0d0d0d] font-mono">{manualInfo.memo}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => copyMemo(manualInfo.memo)} className="rounded-lg border-black/[0.08] text-xs">
                {copied ? <Check className="w-3.5 h-3.5 text-[#1a6b3c]" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#888]">Total</span>
              <span className="font-bold text-[#0d0d0d]">${total}</span>
            </div>
            <p className="text-[11px] text-[#888]">Your payment will be verified within 24 hours. You'll receive an email confirmation.</p>
            <Button onClick={() => { setOpen(false); setManualInfo(null); }} className="w-full rounded-xl bg-[#1a6b3c] hover:bg-[#15562f] text-white">
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="payment-methods">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-[#888]">Amount: ${amount?.toFixed(2)}</span>
              <span className="text-[#888]">+ 3% fee = <strong className="text-[#0d0d0d]">${total}</strong></span>
            </div>

            {METHODS.map(({ id, name, desc, Icon, color }) => (
              <button
                key={id}
                data-testid={`method-${id}`}
                onClick={() => setSelected(id)}
                className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 text-left transition-all ${
                  selected === id ? "border-[#1a6b3c] bg-[#1a6b3c]/5" : "border-black/[0.06] hover:border-black/[0.12]"
                }`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: color + "15" }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[#0d0d0d] text-sm">{name}</p>
                  <p className="text-[11px] text-[#888]">{desc}</p>
                </div>
                {selected === id && <Check className="w-5 h-5 text-[#1a6b3c] flex-shrink-0" />}
              </button>
            ))}

            <Button
              data-testid="confirm-payment-btn"
              disabled={!selected || loading}
              onClick={() => {
                if (selected === "stripe") handlePay();
                else handleManualPay(selected);
              }}
              className="w-full h-12 rounded-xl bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] font-semibold"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay ${total} <ArrowRight className="w-4 h-4 ml-2" /></>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
